import { atom } from 'jotai';

import type { OfflineStorageStatus } from '@/tauri/offline-recording/recording';

/**
 * Why offline transcribing cannot run this session. Only reasons we can state
 * truthfully to a clinician get their own member — a code we were not given is
 * `unknown`, not a guess at the most likely cause.
 *
 * - `outdated-build` — the local database was written by a newer version of
 *   Heidi, so this version refuses to open it. Updating restores the feature.
 * - `unknown` — storage did not open and boot recorded no reason. Nothing about
 *   a fix can be promised.
 */
export type OfflineStorageUnavailableReason = 'outdated-build' | 'unknown';

/**
 * Boot-time availability of offline-recording local storage.
 *
 * `pending` is a real member, not a placeholder: the status is read over IPC
 * after mount, and treating "not answered yet" as either outcome would flash an
 * alarming banner at every launch or hide a genuine refusal on a slow one.
 *
 * Every state here is settled for the life of the app run. The refusal is keyed
 * on state this build cannot change, so nothing transitions back to `available`
 * without a restart on a different build — no consumer should offer a retry.
 */
export type OfflineStorageAvailability =
  | { state: 'pending' }
  | { state: 'available' }
  | { state: 'unavailable'; reason: OfflineStorageUnavailableReason };

/**
 * The `OFFLINE_*` code Rust reports when the on-disk schema version is ahead of
 * the running build. Pinned here as the one place the string is spelled, so a
 * rename shows up as a failing test rather than as copy that quietly stops
 * appearing.
 */
export const OFFLINE_SCHEMA_AHEAD_OF_BUILD_CODE = 'OFFLINE_SCHEMA_AHEAD_OF_BUILD';

/**
 * Hydrated once by `useOfflineStorageAvailabilitySync`, mounted at the app root.
 * Read through `useOfflineStorageAvailability` so the IPC round trip happens
 * once per run rather than once per consuming component.
 */
export const offlineStorageAvailabilityAtom = atom<OfflineStorageAvailability>({
  state: 'pending',
});

/** Map the Rust status DTO onto the state the UI branches on. */
export function toOfflineStorageAvailability(
  status: OfflineStorageStatus
): OfflineStorageAvailability {
  if (status.available) {
    return { state: 'available' };
  }

  return {
    state: 'unavailable',
    reason:
      status.errorCode === OFFLINE_SCHEMA_AHEAD_OF_BUILD_CODE
        ? 'outdated-build'
        : 'unknown',
  };
}
